import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Check, Heart, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { proxyImg } from '@/lib/imageProxy';
import { getCompare, toggleCompare, clearCompare, subscribe, CMP_KEY_NAME } from '@/lib/wishlist';
import { useCart } from '@/lib/cart';

/**
 * Compare page — side-by-side comparison of up to 4 saved products.
 *
 * Rows: image, name, brand, category, price, mrp, discount, stock, score.
 */
export default function Compare() {
    const [ids, setIds] = useState(getCompare());
    const [products, setProducts] = useState([]);
    const { add: addToCart } = useCart();

    useEffect(() => subscribe(CMP_KEY_NAME, () => setIds(getCompare())), []);
    useEffect(() => {
        if (ids.length === 0) { setProducts([]); return; }
        Promise.all(ids.map((id) => api.get(`/catalog/products/${id}`).then(({ data }) => data.product || data).catch(() => null)))
            .then((items) => setProducts(items.filter(Boolean)));
    }, [ids]);

    if (ids.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="compare-empty">
                <Heart size={42} strokeWidth={1.2} className="text-inorbit-orange mb-4" />
                <h2 className="font-serif text-3xl text-inorbit-blue mb-2">Nothing to compare yet</h2>
                <p className="text-text-secondary max-w-md mb-6">
                    Tap the <strong>+ Compare</strong> button on any product card to start a side-by-side comparison of up to 4 items.
                </p>
                <Link to="/shop" className="btn-primary">Browse the catalogue <ArrowRight size={14} /></Link>
            </div>
        );
    }

    const ROWS = [
        { key: 'image', label: '', render: (p) => (
            <div className="aspect-square overflow-hidden bg-cream rounded mb-3">
                <img src={proxyImg(p.image || p.images?.[0])} alt={p.name} className="w-full h-full object-cover" />
            </div>
        ) },
        { key: 'name', label: 'Product', render: (p) => <Link to={`/product/${p.id}`} className="font-serif text-lg text-inorbit-blue hover:text-inorbit-orange line-clamp-2 leading-tight">{p.name}</Link> },
        { key: 'brand', label: 'Brand', render: (p) => <span className="text-sm text-inorbit-orange">{p.brand}</span> },
        { key: 'category', label: 'Category', render: (p) => <span className="text-xs uppercase tracking-widest text-text-tertiary">{p.category}</span> },
        { key: 'price', label: 'Price', render: (p) => <span className="font-serif text-2xl text-inorbit-blue">₹{Number(p.price).toLocaleString('en-IN')}</span> },
        { key: 'mrp', label: 'MRP', render: (p) => p.mrp > p.price ? <span className="line-through text-text-tertiary">₹{Number(p.mrp).toLocaleString('en-IN')}</span> : <span className="text-text-tertiary">—</span> },
        { key: 'discount', label: 'Discount', render: (p) => {
            const d = p.mrp > p.price ? Math.round(100 * (p.mrp - p.price) / p.mrp) : 0;
            return d > 0 ? <span className="pill pill-orange">{d}% off</span> : <span className="text-text-tertiary text-sm">—</span>;
        } },
        { key: 'stock', label: 'Availability', render: (p) => p.stock > 0 ? <span className="pill pill-yellow">In stock</span> : <span className="pill pill-blue">Sold out</span> },
        { key: 'cta', label: '', render: (p) => (
            <div className="flex flex-col gap-1.5 mt-3">
                <button
                    disabled={p.stock <= 0}
                    onClick={async () => { try { await addToCart(p.id, 1); toast.success('Added to bag'); } catch { toast.error('Could not add'); } }}
                    className="btn-primary text-xs justify-center disabled:opacity-50"
                    data-testid={`compare-add-${p.id}`}
                >
                    <ShoppingBag size={12} /> Add to bag
                </button>
                <button onClick={() => toggleCompare(p.id)} className="text-[10px] uppercase tracking-widest text-text-tertiary hover:text-inorbit-orange" data-testid={`compare-remove-${p.id}`}>
                    Remove
                </button>
            </div>
        ) },
    ];

    return (
        <div className="bg-cream min-h-screen" data-testid="compare-page">
            <section className="bg-inorbit-blue-deeper text-white pt-28 pb-10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <div className="overline text-inorbit-yellow mb-2">Side-by-side</div>
                        <h1 className="font-serif text-4xl md:text-5xl" style={{ color: 'white' }}>Compare products</h1>
                        <p className="text-white/75 text-sm mt-2">{products.length} of 4 selected.</p>
                    </div>
                    <button onClick={clearCompare} className="btn-outline border-white text-white hover:bg-white hover:text-inorbit-blue" data-testid="compare-clear-all">
                        Clear all
                    </button>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
                <div className={`grid gap-5`} style={{ gridTemplateColumns: `repeat(${Math.max(2, products.length)}, minmax(0, 1fr))` }}>
                    {products.map((p) => (
                        <div key={p.id} className="card-luxury p-5 relative" data-testid={`compare-card-${p.id}`}>
                            <button onClick={() => toggleCompare(p.id)} className="absolute top-2 right-2 w-7 h-7 bg-cream hover:bg-rose-100 rounded-full flex items-center justify-center text-text-tertiary hover:text-rose-600 z-10" aria-label="Remove">
                                <X size={14} />
                            </button>
                            {ROWS.map((row) => (
                                <div key={row.key} className={row.key === 'image' ? '' : 'mb-3'}>
                                    {row.label && <div className="overline text-[10px] mb-1">{row.label}</div>}
                                    {row.render(p)}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
